import './AlertBanner.scss';
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { checkForSiteAlert } from '../../redux/slices/general';
import { useEnvironment } from '../../hooks/useEnvironment';
import { useInterval } from '../../hooks/useInterval';
import { safeParseJson } from '../../helpers/stringHelpers';
import { SiteAlertSeverity } from '../../enums/SiteAlertSeverity';

const POLLING_INTERVAL_SECONDS = 60;

export const AlertBanner = ({ isSmall }) => {
    const dispatch = useDispatch();
    const { SITE_ALERT_URL } = useEnvironment();
    const siteAlert = useSelector((state) => state.general.siteAlert);
    const userId = useSelector((state) => state.auth?.user?.userId);
    const allocatedClientIds = useSelector((state) => state.auth?.allocatedClientIds);
    const { id, severity, header, message } = siteAlert || {};

    const [isDismissed, setIsDismissed] = useState(true);

    useEffect(() => {
        // initial check
        // small version is for the nav menu which has
        // already been checked with the loading of the nav bar
        if (!SITE_ALERT_URL || isSmall) return;
        dispatch(checkForSiteAlert(SITE_ALERT_URL));
    }, [SITE_ALERT_URL, dispatch, isSmall, userId, allocatedClientIds]);

    useInterval(() => {
        //polling check
        if (!SITE_ALERT_URL) return;
        dispatch(checkForSiteAlert(SITE_ALERT_URL));
    }, POLLING_INTERVAL_SECONDS * 1000);

    useEffect(() => {
        if (!id) return;
        const previousUserId = localStorage.getItem('userId');

        // localStorage always returns strings, so compare as strings to avoid false mismatches
        if (previousUserId !== String(userId)) {
            localStorage.removeItem('dismissedSiteAlerts');
            localStorage.setItem('userId', String(userId));
        }

        const previouslyDismissedAlerts = localStorage.getItem('dismissedSiteAlerts');
        const parsedAlerts = previouslyDismissedAlerts && safeParseJson(previouslyDismissedAlerts);
        const alertPreviouslyDismissed = Array.isArray(parsedAlerts) && parsedAlerts.includes(id);
        setIsDismissed(alertPreviouslyDismissed);
    }, [id, userId]);

    const dismissAlert = (id) => {
        const dismissedAlerts = localStorage.getItem('dismissedSiteAlerts');
        const parsedJSON = dismissedAlerts && safeParseJson(dismissedAlerts);
        const parsedAlerts = Array.isArray(parsedJSON) ? parsedJSON : [];
        if (!parsedAlerts.includes(id)) {
            localStorage.setItem('dismissedSiteAlerts', JSON.stringify([...parsedAlerts, id]));
        }
        setIsDismissed(true);
    };

    const alertBannerClasses = classNames('alert-banner', {
        'alert-banner--severe': severity === SiteAlertSeverity.Severe,
        'alert-banner--medium': severity === SiteAlertSeverity.Medium,
        'alert-banner--low': severity === SiteAlertSeverity.Low,
        'alert-banner--small': isSmall,
        'alert-banner--dismissed': !isSmall && isDismissed,
    });

    if (!id) return null;

    return (
        <div className={alertBannerClasses}>
            <div className="alert-banner__alert-icon-container">
                <div className="alert-banner__alert-icon"></div>
            </div>
            <div className="alert-banner__message">
                <span className="alert-banner__message-header">{header}</span> {message}
            </div>
            {!isSmall && (
                <button onClick={() => dismissAlert(id)} className="alert-banner__action">
                    Dismiss.
                </button>
            )}
        </div>
    );
};

AlertBanner.propTypes = {
    isSmall: PropTypes.bool,
};
